import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  findDrawerByRoute,
  getDrawerTitle,
  getRoutePrefixes,
  DrawerType,
  MenuItemId,
  SidebarBadgeType,
  type ResolvedMenuItem,
} from './MenuConfig';
import FloatingArrowIcon from 'app/components/core/icons/FloatingArrowIcon';
import MenuDrawer from './components/MenuDrawer';
import MobileMenuDrawer from './components/MobileMenuDrawer';
import MobileSidebarHeader from './components/MobileSidebarHeader';
import SidebarContent from './components/SidebarContent';
import SidebarHoverZone from './components/SidebarHoverZone';
import { FloatingDrawerArrow } from './components/SidebarHoverZone/styles';
import { SidebarTooltipStyle } from './components/SidebarIcon/styles';
import {
  useFloatingDrawerHover,
  useResolvedMenu,
  useSidebarDrawerState,
  useSidebarMobile,
} from './hooks';
import {
  trackMenuCategoryExpanded,
  trackMenuItemSelected,
  trackNotificationListOpened,
} from './trackMenuEvents';
import {
  SIDEBAR_MOBILE_BREAKPOINT,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_EXPANDED,
} from './styles';
import * as S from './styles';
import { ROUTES_PATH } from 'app/constants/Routes';
import { useAiPlatformHandoff } from 'app/hooks/useAiPlatformHandoff';
import { GroupIdEnum } from 'app/modules/Segment/types';
import { Tooltip as ReactTooltip } from 'react-tooltip';

type SidebarProps = {
  onWidthChange: (width: number) => void;
};

const Sidebar: React.FC<SidebarProps> = ({ onWidthChange }) => {
  const location = useLocation();
  const history = useHistory();
  const {
    mainItems,
    settingsItems,
    drawerConfig,
    profileMenuItem,
    gamificationMenuItem,
    profileMenuAccountItems,
    showUnifiedProfileMenu,
    isMgmEnabled,
    jurisprudenciaEnabled,
    openPlanExploration,
    planExplorationModal,
    isAcademyUser,
  } = useResolvedMenu();
  const { handoff: handoffToAiPlatform } = useAiPlatformHandoff();

  /* Aluno não tem N2: a gaveta nunca abre, nem pela rota. Sem isto, /cursos
     (item da gaveta Clientes do advogado) abria o painel com os outros
     produtos ao lado do menu do aluno. */
  const drawersEnabled = !isAcademyUser;
  const resolveDrawerByRoute = useCallback(
    (route: string): DrawerType | null =>
      drawersEnabled ? findDrawerByRoute(route) : null,
    [drawersEnabled],
  );
  const sidebarWidth = jurisprudenciaEnabled
    ? SIDEBAR_WIDTH_EXPANDED
    : SIDEBAR_WIDTH;
  const isMobile = useSidebarMobile(SIDEBAR_MOBILE_BREAKPOINT);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const pendingNotificationListOpenRef = useRef<{
    drawerType: DrawerType;
    itemName: string;
  } | null>(null);

  const {
    activeDrawer,
    setActiveDrawer,
    isMenuDrawerCollapsed,
    setIsMenuDrawerCollapsed,
    isDrawerPinned,
    setIsDrawerPinned,
    toggleMenuCollapsed,
    menuDrawerExtendedWidth,
    menuDrawerCollapsedWidth,
  } = useSidebarDrawerState(
    location.pathname,
    onWidthChange,
    sidebarWidth,
    drawersEnabled,
  );

  const hover = useFloatingDrawerHover();
  const {
    hoveredDrawer,
    hoveredIconCenterY,
    setHoveredDrawer,
    handleSidebarHoverEnter,
    handleSidebarHoverLeave,
    handleDrawerIconMouseEnter,
    handleDrawerIconMouseLeave,
    handleFloatingDrawerMouseEnter,
    handleFloatingDrawerMouseLeave,
  } = hover;

  const getActive = (item: ResolvedMenuItem, drawerType?: DrawerType | null) => {
    if (drawerType) {
      return resolveDrawerByRoute(location.pathname) === drawerType;
    }

    const prefixes = getRoutePrefixes(item.route, item.activePrefixes);
    return prefixes.some((prefix) =>
      location.pathname === prefix || location.pathname.startsWith(prefix + '/')
    );
  };

  const floatingState = useMemo(() => {
    const drawerForCurrentRoute = resolveDrawerByRoute(location.pathname);
    const isOnLevel2Route = drawerForCurrentRoute !== null;
    const isHoveringDifferentGroup =
      hoveredDrawer !== null && hoveredDrawer !== drawerForCurrentRoute;
    /* `drawersEnabled` entra na condição porque o hover pode ficar preso em
       `hoveredDrawer` se o usuário virar aluno no meio dele (resolução da flag):
       resolveDrawerByRoute passa a devolver null, isHoveringDifferentGroup fica
       true e a gaveta do advogado continuaria aberta para o aluno. */
    const showFloatingDrawer =
      drawersEnabled &&
      hoveredDrawer !== null &&
      (isHoveringDifferentGroup || (!isDrawerPinned && !isOnLevel2Route));
    return { showFloatingDrawer };
  }, [
    drawersEnabled,
    location.pathname,
    hoveredDrawer,
    isDrawerPinned,
    resolveDrawerByRoute,
  ]);

  const handleIconClick = (targetDrawer: DrawerType | null, route: string) => {
    setIsDrawerPinned(true);
    if (targetDrawer === activeDrawer) {
      const wasCollapsed = isMenuDrawerCollapsed;
      setIsMenuDrawerCollapsed((prev) => !prev);
      if (wasCollapsed && targetDrawer) {
        trackMenuCategoryExpanded(getDrawerTitle(targetDrawer));
      }
    } else {
      if (targetDrawer) {
        trackMenuCategoryExpanded(getDrawerTitle(targetDrawer));
      }
      setActiveDrawer(targetDrawer);
      setIsMenuDrawerCollapsed(false);
    }
    history.push(route);
  };

  const handleNavLinkClick = (route: string) => {
    setActiveDrawer(null);
    setIsDrawerPinned(false);
    history.push(route);
  };

  const handleFloatingDrawerItemClick = (route: string) => {
    if (hoveredDrawer) {
      setActiveDrawer(hoveredDrawer);
      setIsDrawerPinned(true);
      setHoveredDrawer(null);
    }
    history.push(route);
  };

  // Plataforma de IA: a regra mora em `useAiPlatformHandoff`. Aqui só a rota
  // do JusGPT desvia, e sem contexto — o item do menu não tem prompt.
  const desviouParaPlataformaDeIa = (rota: string): boolean => {
    if (rota !== ROUTES_PATH.MAIN.GPT) return false;
    return handoffToAiPlatform();
  };

  const handleItemClick = (item: ResolvedMenuItem) => {
    if (item.disabled) return;
    if (item.drawerType) {
      const opensDrawer =
        item.drawerType !== activeDrawer || isMenuDrawerCollapsed;
      if (
        opensDrawer &&
        item.badge?.kind === SidebarBadgeType.NOTIFICATION_TAG
      ) {
        pendingNotificationListOpenRef.current = {
          drawerType: item.drawerType,
          itemName: item.label,
        };
      }
      handleIconClick(item.drawerType, item.route);
    } else {
      trackMenuItemSelected({
        groupId: item.trackEvent?.groupId ?? GroupIdEnum.GENERAL_GROUP,
        itemName: item.label,
        categoryParent: null,
      });
      // O tracking acontece nos dois caminhos: pro Segment, o advogado clicou no
      // mesmo item do menu — o destino é detalhe de implementação do piloto.
      if (
        item.id === MenuItemId.IA_JUSFY &&
        desviouParaPlataformaDeIa(item.route)
      ) {
        return;
      }
      handleNavLinkClick(item.route);
    }
  };

  const handleDrawerItemClick = (path: string) => {
    const drawerType = resolveDrawerByRoute(path);
    if (drawerType !== null) {
      setActiveDrawer(drawerType);
      if (drawerType !== activeDrawer) {
        setIsMenuDrawerCollapsed(false);
      }
      history.push(path);
    } else {
      handleNavLinkClick(path);
    }
  };

  const handleLogoClick = () => {
    handleNavLinkClick(ROUTES_PATH.MAIN.DASHBOARD);
  };

  const sidebarContentProps = {
    mainItems,
    settingsItems,
    profileMenuItem,
    gamificationMenuItem,
    profileMenuAccountItems,
    showUnifiedProfileMenu,
    showMgmInviteAction: isMgmEnabled,
    getActive,
    onItemClick: handleItemClick,
    onNavigate: handleNavLinkClick,
    onOpenPlanExploration: openPlanExploration,
    onDrawerIconMouseEnter: handleDrawerIconMouseEnter,
    onDrawerIconMouseLeave: handleDrawerIconMouseLeave,
    onLogoClick: handleLogoClick,
  };

  useEffect(() => {
    return () => {
      document.body.classList.remove('drawer-open-no-scroll');
    };
  }, []);

  useEffect(() => {
    const pendingNotificationListOpen = pendingNotificationListOpenRef.current;
    if (!pendingNotificationListOpen) return;

    if (
      pendingNotificationListOpen.drawerType === activeDrawer &&
      !isMenuDrawerCollapsed
    ) {
      trackNotificationListOpened({
        item_name: pendingNotificationListOpen.itemName,
      });
      pendingNotificationListOpenRef.current = null;
      return;
    }

    if (
      activeDrawer !== null &&
      activeDrawer !== pendingNotificationListOpen.drawerType
    ) {
      pendingNotificationListOpenRef.current = null;
    }
  }, [activeDrawer, isMenuDrawerCollapsed]);

  if (isMobile) {
    return (
      <>
        <MobileSidebarHeader
          onMenuToggle={() => setIsMobileDrawerOpen((prev) => !prev)}
          isMenuOpen={isMobileDrawerOpen}
        />
        <MobileMenuDrawer
          isOpen={isMobileDrawerOpen}
          onClose={() => setIsMobileDrawerOpen(false)}
          onNavigate={(path) => {
            // O drawer mobile só entrega o path, então a chave do desvio é a
            // rota do JusGPT — não o id do item.
            if (desviouParaPlataformaDeIa(path)) return;
            history.push(path);
          }}
        />
      </>
    );
  }

  return (
    <>
      <SidebarTooltipStyle />
      <ReactTooltip
        id="sidebar-icon-tooltip"
        place="right"
        className="sidebar-custom-tooltip"
      />
      <SidebarHoverZone
        width={sidebarWidth}
        onMouseEnter={handleSidebarHoverEnter}
        onMouseLeave={handleSidebarHoverLeave}
      >
        <S.SidebarWrapper $width={sidebarWidth}>
          <SidebarContent {...sidebarContentProps} />
        </S.SidebarWrapper>
      </SidebarHoverZone>
      {floatingState.showFloatingDrawer && hoveredDrawer && (
        <>
          <S.FloatingDrawerBridge
            $left={sidebarWidth}
            $width={menuDrawerExtendedWidth}
            onMouseEnter={handleFloatingDrawerMouseEnter}
            onMouseLeave={handleFloatingDrawerMouseLeave}
            aria-hidden
          />
          <MenuDrawer
            isOpen={true}
            isCollapsed={false}
            toggleMenuCollapsed={toggleMenuCollapsed}
            currentAsideWidth={sidebarWidth}
            menuDrawerExtendedWidth={menuDrawerExtendedWidth}
            menuDrawerCollapsedWidth={menuDrawerCollapsedWidth}
            isFloating={true}
            activeDrawerType={hoveredDrawer}
            drawerConfig={drawerConfig}
            onNavigate={handleFloatingDrawerItemClick}
            onMouseEnter={handleFloatingDrawerMouseEnter}
            onMouseLeave={handleFloatingDrawerMouseLeave}
          />
          {hoveredIconCenterY != null && (
            <FloatingDrawerArrow
              $sidebarWidth={sidebarWidth}
              $top={hoveredIconCenterY}
              aria-hidden
            >
              <FloatingArrowIcon />
            </FloatingDrawerArrow>
          )}
        </>
      )}

      {activeDrawer && isDrawerPinned && (
        <MenuDrawer
          isOpen={true}
          isCollapsed={isMenuDrawerCollapsed}
          toggleMenuCollapsed={toggleMenuCollapsed}
          currentAsideWidth={sidebarWidth}
          menuDrawerExtendedWidth={menuDrawerExtendedWidth}
          menuDrawerCollapsedWidth={menuDrawerCollapsedWidth}
          activeDrawerType={activeDrawer}
          drawerConfig={drawerConfig}
          onNavigate={handleDrawerItemClick}
        />
      )}
      {planExplorationModal}
    </>
  );
};

export default Sidebar;
